import React from 'react';
import { motion } from 'framer-motion';
import {
  TrendingDown, Sparkles, Flame, Droplet, Wheat, Beef, Trophy,
  AlertTriangle, CheckCircle2,
} from 'lucide-react';

const ICONS = { flame: Flame, droplet: Droplet, wheat: Wheat, beef: Beef, trophy: Trophy };
const SEV = {
  success: { color: '#10B981', bg: 'from-emerald-500/12', border: 'border-emerald-500/30', ring: 'bg-emerald-500/15' },
  warning: { color: '#F59E0B', bg: 'from-amber-500/12', border: 'border-amber-500/30', ring: 'bg-amber-500/15' },
  danger: { color: '#EF4444', bg: 'from-red-500/12', border: 'border-red-500/30', ring: 'bg-red-500/15' },
  info: { color: '#3B82F6', bg: 'from-blue-500/12', border: 'border-blue-500/30', ring: 'bg-blue-500/15' },
};

const CarryoverBanner = ({ c, en }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
    className="relative overflow-hidden rounded-3xl border border-yellow-500/30 bg-gradient-to-br from-primary/15 via-zinc-900 to-black p-5 sm:p-6"
    data-testid="carryover-banner"
  >
    <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-yellow-400/15 blur-3xl pointer-events-none" />
    <div className="relative flex items-start gap-4">
      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-primary flex items-center justify-center shrink-0">
        <TrendingDown className="w-6 h-6 text-black" strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-500/30 mb-2">
          <Sparkles className="w-3 h-3 text-yellow-400" />
          <span className="text-[10px] font-bold tracking-wider text-yellow-300">{en ? 'SMART ADJUSTMENT' : 'AJUSTEMENT INTELLIGENT'}</span>
        </div>
        <h3 className="calc-display text-xl sm:text-2xl font-black text-white leading-tight">{c.title}</h3>
        <div className="flex items-end gap-3 my-2">
          <span className="text-4xl font-black text-white tabular-nums leading-none" data-testid="adjusted-target">{c.adjusted_target}</span>
          <span className="text-sm text-zinc-500 mb-1">kcal</span>
          <div className="text-xs mb-0.5">
            <p className="line-through text-zinc-600">{c.base_target} kcal</p>
            <p className="text-yellow-400 font-bold">−{c.surplus} kcal</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">{c.message}</p>
      </div>
    </div>
  </motion.div>
);

const TipCard = ({ t, i }) => {
  const sev = SEV[t.severity] || SEV.info;
  const Icon = ICONS[t.icon] || (t.severity === 'success' ? CheckCircle2 : AlertTriangle);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
      className={`rounded-2xl border ${sev.border} bg-gradient-to-br ${sev.bg} to-zinc-900 p-4`}
      data-testid="coaching-tip"
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-xl ${sev.ring} flex items-center justify-center shrink-0`}>
          <Icon className="w-4 h-4" style={{ color: sev.color }} />
        </div>
        <div className="min-w-0">
          <p className="font-bold text-white text-sm">{t.title}</p>
          <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">{t.message}</p>
        </div>
      </div>
    </motion.div>
  );
};

const FoodFlags = ({ flags, en }) => (
  <div data-testid="coaching-food-flags">
    <h4 className="flex items-center gap-2 text-sm font-bold text-white mb-3">
      <AlertTriangle className="w-4 h-4 text-amber-400" /> {en ? 'Foods to watch' : 'Aliments à surveiller'}
    </h4>
    <div className="space-y-2">
      {flags.map((f, i) => (
        <motion.div
          key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
          className="rounded-xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-3 flex items-start gap-3"
          data-testid="food-flag"
        >
          <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0 mt-0.5">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-white text-sm truncate">{f.name}</p>
              {f.score != null && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/10 text-zinc-300 shrink-0">{f.score}/100</span>}
            </div>
            <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">{f.reason}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

export const CoachingPanel = ({ coaching, en }) => {
  if (!coaching) return null;
  const { carryover, tips = [], food_flags = [] } = coaching;
  if (!carryover && tips.length === 0 && food_flags.length === 0) return null;
  return (
    <div className="space-y-5" data-testid="coaching-panel">
      {carryover && <CarryoverBanner c={carryover} en={en} />}
      {tips.length > 0 && (
        <div data-testid="coaching-tips">
          <h4 className="flex items-center gap-2 text-sm font-bold text-white mb-3">
            <Sparkles className="w-4 h-4 text-primary" /> {en ? "Today's coaching" : 'Conseils du jour'}
          </h4>
          <div className="grid sm:grid-cols-2 gap-3">
            {tips.map((t, i) => <TipCard key={i} t={t} i={i} />)}
          </div>
        </div>
      )}
      {food_flags.length > 0 && <FoodFlags flags={food_flags} en={en} />}
    </div>
  );
};

export default CoachingPanel;
